/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   PipelineHealthOverview.tsx
 *     Feature         :   191-pipeline-monitoring-platform
 *     Description     :   Domain-grouped health grid over the monitoring registry — one
 *                         card per data-movement process: latest-run status, 7-day success
 *                         rate, duration, stale/stuck badges (display-only; thresholds from
 *                         the registry row), observed-cadence hint, presence indicator.
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import React from 'react';
import type {
  PipelineDomainGroup,
  MonitoredPipelineWithHealth,
} from '@shared/types/monitoredPipelines';
import {
  PipelineStatusBadge,
  HealthConditionBadge,
  formatDuration,
  formatDateTime,
} from './PipelineStatusBadge';

export interface PipelineHealthOverviewProps {
  domains: PipelineDomainGroup[];
  selectedKey?: string | null;
  onSelectPipeline?: (pipelineKey: string) => void;
}

function successRateClasses(rate: number | null): string {
  if (rate === null) return 'text-[var(--color-text-secondary)]';
  if (rate >= 0.95) return 'text-green-600 dark:text-green-400';
  if (rate >= 0.8)  return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function PipelineCard({
  pipeline,
  isSelected,
  onSelect,
}: {
  pipeline: MonitoredPipelineWithHealth;
  isSelected: boolean;
  onSelect?: (key: string) => void;
}) {
  const { health } = pipeline;
  const latest = health.latestRun;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(pipeline.pipelineKey)}
      disabled={!pipeline.presentInEnv}
      data-testid={`pipeline-card-${pipeline.pipelineKey}`}
      className={`text-left rounded-lg border p-4 transition-colors w-full ${
        !pipeline.presentInEnv
          ? 'border-dashed border-[var(--color-border)] bg-[var(--color-bg-subtle)] opacity-60 cursor-not-allowed'
          : isSelected
            ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/50 dark:bg-blue-900/20'
            : 'border-[var(--color-border)] bg-[var(--color-bg-card)] hover:border-blue-300 dark:hover:border-blue-600'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-[var(--color-text-primary)] truncate">
            {pipeline.displayName}
          </div>
          <div className="text-xs text-[var(--color-text-secondary)] font-mono truncate">
            {pipeline.pipelineKey}
            {pipeline.instanceLabel ? ` · ${pipeline.instanceLabel}` : ''}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {latest && <PipelineStatusBadge status={latest.status} />}
          <div className="flex gap-1">
            {health.isStale && (
              <HealthConditionBadge
                kind="stale"
                title={`No successful run within ${pipeline.freshnessHours ?? '—'}h (registry freshness_hours)`}
              />
            )}
            {health.isStuck && (
              <HealthConditionBadge
                kind="stuck"
                title={`Running beyond ${pipeline.maxRunMinutes ?? '—'}min (registry max_run_minutes)`}
              />
            )}
          </div>
        </div>
      </div>

      {!pipeline.presentInEnv ? (
        <div className="mt-3 text-xs text-[var(--color-text-secondary)] italic">
          Not present in this environment
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <div>
            <div className="text-[var(--color-text-secondary)]">Success (7d)</div>
            <div className={`font-semibold ${successRateClasses(health.successRate7d)}`}>
              {health.successRate7d === null ? '—' : `${Math.round(health.successRate7d * 100)}%`}
            </div>
          </div>
          <div>
            <div className="text-[var(--color-text-secondary)]">Last run</div>
            <div className="font-medium text-[var(--color-text-primary)]" title={latest ? formatDateTime(latest.startTime) : undefined}>
              {latest ? formatDateTime(latest.startTime) : '—'}
            </div>
          </div>
          <div>
            <div className="text-[var(--color-text-secondary)]">Duration</div>
            <div className="font-medium text-[var(--color-text-primary)]">
              {latest ? formatDuration(latest.durationSeconds) : '—'}
            </div>
          </div>
        </div>
      )}

      {pipeline.presentInEnv && health.observedCadenceHours !== null && (
        <div
          className="mt-2 text-[11px] text-[var(--color-text-secondary)]"
          title="Median gap between the last 20 runs — a hint for tuning freshness_hours"
        >
          Observed cadence ≈ every {health.observedCadenceHours}h
          {pipeline.expectedCadence ? ` · expected: ${pipeline.expectedCadence}` : ''}
        </div>
      )}
    </button>
  );
}

export function PipelineHealthOverview({
  domains,
  selectedKey = null,
  onSelectPipeline,
}: PipelineHealthOverviewProps) {
  if (domains.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-secondary)]">
        No monitored pipelines registered. Add a row to dbo.monitored_pipelines to onboard a process.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pipeline-health-overview">
      {domains.map((group) => (
        <div key={group.domain}>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] mb-2">
            {group.domain}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.pipelines.map((p) => (
              <PipelineCard
                key={p.pipelineKey}
                pipeline={p}
                isSelected={selectedKey === p.pipelineKey}
                onSelect={onSelectPipeline}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
